import { formatDateTime } from "@/api/datetime";
import { AddWorkerDialog } from "@/components/AddWorkerDialog";
import { CreateWorkerAccountDialog } from "@/components/CreateWorkerAccountDialog";
import { MultiSelectCombobox } from "@/components/shadcn-custom/MultiSelectCombobox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { SearchableSelectPopover } from "@/components/ui/searchable-select-popover";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPhoneNumber } from "@/lib/utils";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { collection, onSnapshot, query, Timestamp } from "firebase/firestore";
import { db } from "../../../../firebase";
import { ArrowUpDown, UserPlus, X } from "lucide-react";
import { parseAsArrayOf, parseAsString, parseAsStringEnum, useQueryStates } from "nuqs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isWorkerAssignedToProject,
  type ProjectModel,
  type WorkerModel,
} from "../../../../firestore_types";
import {
  useAdminAgencies,
  useAdminProjects,
  useAdminUsers,
  useAdminWorkers,
} from "../-admin-context";

export const Route = createFileRoute("/dashboard/admin/workers/")({
  component: RouteComponent,
});

interface ClockInData {
  workerId: string;
  clockedIn?: boolean;
  clockedOut?: boolean;
  markedSkipped?: boolean;
  timestamp: Timestamp;
}

function RouteComponent() {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false);

  // Hooks
  const navigate = useNavigate();
  const workersResource = useAdminWorkers();
  const usersResource = useAdminUsers();
  const projectsResource = useAdminProjects();
  const agenciesResource = useAdminAgencies();

  const loading = workersResource.tag === "loading";
  const workers = workersResource.tag === "success" ? workersResource.asData : [];
  const users = usersResource.tag === "success" ? usersResource.asData : [];
  const projects = projectsResource.tag === "success" ? projectsResource.asData : [];
  const agencies = agenciesResource.tag === "success" ? agenciesResource.asData : [];

  // Use nuqs for URL state management
  const [filters, setFilters] = useQueryStates(
    {
      search: parseAsString.withDefault(""),
      status: parseAsStringEnum(["all", "test", "live"]).withDefault("all"),
      type: parseAsStringEnum(["all", "app_worker", "regular"]).withDefault("all"),
      manager: parseAsString.withDefault("all"),
      agency: parseAsString.withDefault("all"),
      experience: parseAsArrayOf(parseAsString).withDefault([]),
      gender: parseAsStringEnum(["all", "Male", "Female"]).withDefault("all"),
      location: parseAsString.withDefault("all"),
      englishLevel: parseAsString.withDefault("all"),
      hasDriverLicense: parseAsStringEnum(["all", "Yes", "No"]).withDefault("all"),
      hasCar: parseAsStringEnum(["all", "Yes", "No"]).withDefault("all"),
    },
    { history: "replace" },
  );

  // Extract filter values
  const search = filters.search;
  const statusFilter = filters.status;
  const typeFilter = filters.type;
  const managerFilter = filters.manager;
  const agencyFilter = filters.agency;
  const experienceFilter = filters.experience;
  const genderFilter = filters.gender;
  const locationFilter = filters.location;
  const englishLevelFilter = filters.englishLevel;
  const driverLicenseFilter = filters.hasDriverLicense;
  const hasCarFilter = filters.hasCar;

  // Clock-in state
  const [clockInsData, setClockInsData] = useState<
    Map<string, { skipped: number; inProgress: number }>
  >(new Map());
  const [loadingClockIns, setLoadingClockIns] = useState(true);

  // Local search state drives the column filter instantly; URL is updated lazily
  const [localSearch, setLocalSearch] = useState(search);
  const urlDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local state if URL search changes externally (e.g. back/forward navigation)
  const prevUrlSearch = useRef(search);
  useEffect(() => {
    if (search !== prevUrlSearch.current) {
      prevUrlSearch.current = search;
      setLocalSearch(search);
    }
  }, [search]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (urlDebounceTimer.current) clearTimeout(urlDebounceTimer.current);
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
    if (urlDebounceTimer.current) clearTimeout(urlDebounceTimer.current);
    urlDebounceTimer.current = setTimeout(() => {
      setFilters({ search: value });
    }, 300);
  };

  const assignedProjectsMap = useMemo(() => {
    const map = new Map<string, ProjectModel[]>();
    if (!workers || !projects) {
      return map;
    }

    for (const worker of workers) {
      map.set(
        worker.id,
        projects.filter((project) => isWorkerAssignedToProject(project, worker.id)),
      );
    }

    return map;
  }, [projects, workers]);

  // Load clock-ins and count skipped/in-progress
  useEffect(() => {
    if (workers.length === 0) {
      setLoadingClockIns(false);
      return;
    }

    const clockInsQuery = query(collection(db, "clockIns"));

    const unsub = onSnapshot(
      clockInsQuery,
      (snapshot) => {
        const now = Date.now();
        const countsMap = new Map<string, { skipped: number; inProgress: number }>();

        // Initialize all workers with 0 counts
        workers.forEach((worker) => {
          countsMap.set(worker.id, { skipped: 0, inProgress: 0 });
        });

        // Count clock-ins for each worker
        snapshot.docs.forEach((doc) => {
          const clockIn = doc.data() as ClockInData;
          const workerId = clockIn.workerId;

          if (!countsMap.has(workerId)) {
            countsMap.set(workerId, { skipped: 0, inProgress: 0 });
          }

          const counts = countsMap.get(workerId)!;
          const timestampMillis = clockIn.timestamp.toMillis();

          // Skipped: markedSkipped === true OR (clockedIn !== true && timestamp < now)
          if (
            clockIn.markedSkipped === true ||
            (clockIn.clockedIn !== true && timestampMillis < now)
          ) {
            counts.skipped++;
          }

          // In-progress: clockedIn === true && clockedOut !== true && markedSkipped !== true
          if (
            clockIn.clockedIn === true &&
            clockIn.clockedOut !== true &&
            clockIn.markedSkipped !== true
          ) {
            counts.inProgress++;
          }
        });

        setClockInsData(countsMap);
        setLoadingClockIns(false);
      },
      (error) => {
        console.error("Error loading clock-ins:", error);
        setLoadingClockIns(false);
      },
    );

    return () => unsub();
  }, [workers]);

  // Helper functions
  const getManagerName = useCallback(
    (managerId?: string) => {
      if (!managerId) {
        return "No Manager";
      }
      const manager = users?.find((user) => user.uid === managerId && user.type === "manager");
      return manager?.name || "Unknown Manager";
    },
    [users],
  );

  const getAgencyName = useCallback(
    (agencyId: string) => {
      if (!agencyId) return "No Agency";
      const agency = agencies?.find((agency) => agency.id === agencyId);
      return agency?.name || "Unknown Agency";
    },
    [agencies],
  );

  const getAssignedProjects = useCallback(
    (workerId: string) => {
      return assignedProjectsMap.get(workerId) || [];
    },
    [assignedProjectsMap],
  );

  const getManagerOptions = useCallback(() => {
    return users?.filter((user) => user.type === "manager") || [];
  }, [users]);

  const handleWorkerAdded = (workerId?: string) => {
    // Workers will automatically update via real-time listener
    if (workerId) {
      navigate({
        to: "/dashboard/admin/workers/$workerId",
        params: { workerId },
      });
    }
  };

  // Helper function to clear all filters
  const clearFilters = () => {
    setFilters({
      status: "all",
      type: "all",
      manager: "all",
      agency: "all",
      experience: [],
      gender: "all",
      location: "all",
      englishLevel: "all",
      hasDriverLicense: "all",
      hasCar: "all",
    });
  };

  // Helper function to check if any filters are active
  const hasActiveFilters = () => {
    return (
      statusFilter !== "all" ||
      typeFilter !== "all" ||
      managerFilter !== "all" ||
      agencyFilter !== "all" ||
      experienceFilter.length > 0 ||
      genderFilter !== "all" ||
      locationFilter !== "all" ||
      englishLevelFilter !== "all" ||
      driverLicenseFilter !== "all" ||
      hasCarFilter !== "all"
    );
  };

  // Helper function to get worker initials for avatar fallback
  const getWorkerInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Filter and sort workers
  const filteredWorkers = useMemo(() => {
    if (!workers) {
      return [];
    }

    return [...workers]
      .filter((worker) => {
        if (statusFilter !== "all") {
          const isTestNumber = worker.isTestNumber || false;
          if (statusFilter === "test" && !isTestNumber) return false;
          if (statusFilter === "live" && isTestNumber) return false;
        }

        if (typeFilter !== "all") {
          const workerType = worker.type || "regular";
          if (typeFilter === "app_worker" && workerType !== "app_worker") return false;
          if (typeFilter === "regular" && workerType === "app_worker") return false;
        }

        if (managerFilter !== "all" && worker.managerId !== managerFilter) {
          return false;
        }

        if (agencyFilter !== "all") {
          if (agencyFilter === "none" && worker.agencyId) return false;
          if (agencyFilter !== "none" && worker.agencyId !== agencyFilter) return false;
        }

        if (experienceFilter.length > 0) {
          if (
            !worker.specialties ||
            !experienceFilter.some((specialty) => worker.specialties?.includes(specialty))
          ) {
            return false;
          }
        }

        if (genderFilter !== "all") {
          if (worker.gender !== genderFilter) {
            return false;
          }
        }

        if (locationFilter !== "all") {
          if (
            !worker.location ||
            !worker.location.toLowerCase().includes(locationFilter.toLowerCase())
          ) {
            return false;
          }
        }

        if (englishLevelFilter !== "all") {
          if (worker.englishLevel !== englishLevelFilter) {
            return false;
          }
        }

        if (driverLicenseFilter !== "all") {
          if (worker.hasDriverLicense !== driverLicenseFilter) {
            return false;
          }
        }

        if (hasCarFilter !== "all") {
          if (worker.hasCar !== hasCarFilter) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    agencyFilter,
    genderFilter,
    managerFilter,
    experienceFilter,
    statusFilter,
    typeFilter,
    workers,
    locationFilter,
    englishLevelFilter,
    driverLicenseFilter,
    hasCarFilter,
  ]);

  const columns = useMemo<ColumnDef<WorkerModel>[]>(
    () => [
      {
        id: "all",
        accessorFn: (worker) => {
          const assignedProjects = getAssignedProjects(worker.id);
          const parts = [
            worker.name,
            worker.phoneNumber,
            worker.email ?? "",
            getManagerName(worker.managerId),
            getAgencyName(worker.agencyId || ""),
            ...(worker.specialties ?? []),
            ...assignedProjects.map((project) => project.name),
            worker.location ?? "",
            worker.englishLevel ?? "",
            worker.hasDriverLicense ?? "",
            worker.hasCar ?? "",
            worker.notes ?? "",
          ];
          return parts.filter(Boolean).join(" ");
        },
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: true,
      },
      {
        accessorKey: "name",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Worker
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const worker = row.original;

          return (
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={worker.profilePicture || undefined} alt={worker.name} />
                <AvatarFallback className="text-xs font-medium">
                  {getWorkerInitials(worker.name)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1 text-sm">
                <Link
                  to="/dashboard/admin/workers/$workerId"
                  params={{ workerId: worker.id }}
                  className="text-primary font-medium hover:underline"
                >
                  {worker.name}
                </Link>
                {worker.type === "app_worker" && (
                  <div className="text-muted-foreground text-xs">{worker.email}</div>
                )}
                <div className="text-muted-foreground text-xs">
                  {formatPhoneNumber(worker.phoneNumber)}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "manager",
        accessorFn: (worker) => getManagerName(worker.managerId),
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Manager
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <div className="text-sm">{getManagerName(row.original.managerId)}</div>,
      },
      {
        id: "gender",
        accessorKey: "gender",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Gender
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <div className="text-sm">{row.original.gender || "-"}</div>,
      },
      {
        id: "agency",
        accessorFn: (worker) => getAgencyName(worker.agencyId || ""),
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Agency
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const agencyId = row.original.agencyId;

          if (!agencyId) {
            return <div className="text-sm">No Agency</div>;
          }

          return (
            <Link
              to="/dashboard/admin/agencies/$agencyId"
              params={{ agencyId }}
              className="text-primary text-sm hover:underline"
            >
              {getAgencyName(agencyId)}
            </Link>
          );
        },
      },
      {
        id: "projectCount",
        accessorFn: (worker) => getAssignedProjects(worker.id).length,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Assigned Projects
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const projects = getAssignedProjects(row.original.id) ?? [];
          if (projects.length === 0) return <span>No projects</span>;
          return (
            <div className="flex flex-col">
              {projects.map((p) => (
                <Link
                  to="/dashboard/admin/projects/$projectId"
                  className="text-primary hover:underline"
                  params={{ projectId: p.id }}
                  key={p.id}
                >
                  {p.name}
                </Link>
              ))}
            </div>
          );
        },
      },
      {
        id: "payRate",
        accessorFn: (worker) => worker.payRate ?? 0,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Pay Rate ($/hr)
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.payRate != null ? `$${row.original.payRate.toFixed(2)}/hr` : "-"}
          </div>
        ),
      },
      {
        id: "billingRate",
        accessorFn: (worker) => worker.billingRate ?? 0,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Billing Rate ($/hr)
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => (
          <div className="text-sm">
            {row.original.billingRate != null ? `$${row.original.billingRate.toFixed(2)}/hr` : "-"}
          </div>
        ),
      },
      {
        id: "specialties",
        header: "Worker experience",
        cell: ({ row }) => {
          const specialties = row.original.specialties ?? [];

          if (specialties.length === 0) {
            return <span className="text-muted-foreground text-xs italic">None</span>;
          }

          return (
            <div className="flex max-w-32 flex-wrap gap-1">
              {specialties.slice(0, 3).map((specialty) => (
                <Badge key={specialty} variant="outline" className="text-xs">
                  {specialty}
                </Badge>
              ))}
              {specialties.length > 3 && (
                <Badge variant="secondary" className="text-xs">
                  +{specialties.length - 3}
                </Badge>
              )}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: "location",
        header: "Location",
        cell: ({ row }) => {
          const location = row.original.location;
          return location ? (
            <div className="text-sm">{location}</div>
          ) : (
            <span className="text-muted-foreground text-xs italic">-</span>
          );
        },
        enableSorting: false,
      },
      {
        id: "englishLevel",
        header: "English",
        cell: ({ row }) => {
          const level = row.original.englishLevel;
          return level ? (
            <Badge variant="secondary" className="text-xs">
              {level}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-xs italic">-</span>
          );
        },
        enableSorting: false,
      },
      {
        id: "hasDriverLicense",
        header: "License",
        cell: ({ row }) => {
          const hasLicense = row.original.hasDriverLicense;
          return hasLicense ? (
            <Badge variant={hasLicense === "Yes" ? "default" : "outline"} className="text-xs">
              {hasLicense}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-xs italic">-</span>
          );
        },
        enableSorting: false,
      },
      {
        id: "hasCar",
        header: "Car",
        cell: ({ row }) => {
          const hasCar = row.original.hasCar;
          return hasCar ? (
            <Badge variant={hasCar === "Yes" ? "default" : "outline"} className="text-xs">
              {hasCar}
            </Badge>
          ) : (
            <span className="text-muted-foreground text-xs italic">-</span>
          );
        },
        enableSorting: false,
      },
      {
        id: "createdAt",
        accessorFn: (worker) =>
          worker.createdAt ? ((worker.createdAt as Timestamp).toMillis?.() ?? 0) : 0,
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Created
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => <div className="text-sm">{formatDateTime(row.original.createdAt)}</div>,
      },
      {
        id: "skippedClockIns",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            Skipped
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        accessorFn: (row) => {
          const counts = clockInsData.get(row.id);
          return counts?.skipped ?? 0;
        },
        cell: ({ row }) => {
          const counts = clockInsData.get(row.original.id);
          const skipped = counts?.skipped ?? 0;

          if (loadingClockIns) {
            return <Skeleton className="h-6 w-12" />;
          }

          return (
            <Badge variant={skipped > 0 ? "destructive" : "secondary"} className="text-xs">
              {skipped}
            </Badge>
          );
        },
        sortingFn: (rowA, rowB) => {
          const aSkipped = clockInsData.get(rowA.original.id)?.skipped ?? 0;
          const bSkipped = clockInsData.get(rowB.original.id)?.skipped ?? 0;
          return aSkipped - bSkipped;
        },
      },
      {
        id: "inProgressClockIns",
        header: ({ column }) => (
          <Button
            variant="ghost"
            className="h-auto p-0 font-semibold"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            In Progress
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        accessorFn: (row) => {
          const counts = clockInsData.get(row.id);
          return counts?.inProgress ?? 0;
        },
        cell: ({ row }) => {
          const counts = clockInsData.get(row.original.id);
          const inProgress = counts?.inProgress ?? 0;

          if (loadingClockIns) {
            return <Skeleton className="h-6 w-12" />;
          }

          return (
            <Badge variant={inProgress > 0 ? "default" : "secondary"} className="text-xs">
              {inProgress}
            </Badge>
          );
        },
        sortingFn: (rowA, rowB) => {
          const aInProgress = clockInsData.get(rowA.original.id)?.inProgress ?? 0;
          const bInProgress = clockInsData.get(rowB.original.id)?.inProgress ?? 0;
          return aInProgress - bInProgress;
        },
      },
    ],
    [getAgencyName, getAssignedProjects, getManagerName, clockInsData, loadingClockIns],
  );

  // Get unique experiences from all workers
  const uniqueExperiences = useMemo(() => {
    const experiencesSet = new Set<string>();
    workers.forEach((worker) => {
      if (worker.specialties) {
        worker.specialties.forEach((specialty) => experiencesSet.add(specialty));
      }
    });
    return Array.from(experiencesSet)
      .sort()
      .map((exp) => ({ value: exp, label: exp }));
  }, [workers]);

  const [showFilters, setShowFilters] = useState(false);

  if (loading) {
    return <div className="p-1 sm:p-6">Loading workers...</div>;
  }

  return (
    <div className="space-y-6 p-1 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold">All Workers Management</h1>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={() => setIsCreateAccountModalOpen(true)}
            className="flex items-center justify-center gap-2 rounded bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-600/80"
          >
            <UserPlus className="h-4 w-4" />
            <span className="hidden sm:inline">Create Worker Account</span>
            <span className="sm:hidden">Create Account</span>
          </button>
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-primary hover:bg-primary/80 rounded px-4 py-2 text-sm text-white"
          >
            Add Worker
          </button>
        </div>
      </div>

      {/* Filter Toggle Button */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className="gap-2"
        >
          <X className={`h-4 w-4 transition-transform ${showFilters ? "rotate-0" : "rotate-45"}`} />
          {showFilters ? "Hide Filters" : "Show Filters"}
          {hasActiveFilters() && (
            <Badge variant="default" className="ml-1">
              {[
                statusFilter !== "all" ? 1 : 0,
                typeFilter !== "all" ? 1 : 0,
                managerFilter !== "all" ? 1 : 0,
                agencyFilter !== "all" ? 1 : 0,
                experienceFilter.length,
                genderFilter !== "all" ? 1 : 0,
                locationFilter !== "all" ? 1 : 0,
                englishLevelFilter !== "all" ? 1 : 0,
                driverLicenseFilter !== "all" ? 1 : 0,
                hasCarFilter !== "all" ? 1 : 0,
              ].reduce((a, b) => a + b, 0)}
            </Badge>
          )}
        </Button>
        {hasActiveFilters() && (
          <Button variant="outline" size="sm" className="gap-1" onClick={clearFilters}>
            <X className="h-4 w-4" /> Clear All
          </Button>
        )}
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 md:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {/* Manager Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Manager</label>
              <SearchableSelectPopover
                items={getManagerOptions() || []}
                selectedValue={managerFilter === "all" ? null : managerFilter}
                onSelect={(value) => setFilters({ manager: value || "all" })}
                getKey={(manager) => manager.uid}
                getLabel={(manager) => manager.name}
                triggerLabel={
                  managerFilter === "all" ? "All Managers" : getManagerName(managerFilter)
                }
                placeholder="Search managers..."
                emptyText="No managers found"
                allOption={{ value: null, label: "All Managers" }}
                buttonClassName="w-full h-9 justify-between"
                contentClassName="w-full p-0"
              />
            </div>

            {/* Agency Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Agency</label>
              <SearchableSelectPopover
                items={[{ id: "none", name: "No Agency" }, ...(agencies || [])]}
                selectedValue={agencyFilter === "all" ? null : agencyFilter}
                onSelect={(value) => setFilters({ agency: value || "all" })}
                getKey={(item) => (item.id === "none" ? "none" : item.id)}
                getLabel={(item) => item.name}
                triggerLabel={
                  agencyFilter === "all"
                    ? "All Agencies"
                    : agencyFilter === "none"
                      ? "No Agency"
                      : getAgencyName(agencyFilter)
                }
                placeholder="Search agencies..."
                emptyText="No agencies found"
                allOption={{ value: null, label: "All Agencies" }}
                buttonClassName="w-full h-9 justify-between"
                contentClassName="w-full p-0"
              />
            </div>

            {/* Experience Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Experience</label>
              <MultiSelectCombobox
                value={experienceFilter}
                onChange={(newExperience) => setFilters({ experience: newExperience })}
                options={uniqueExperiences}
                placeholder={
                  experienceFilter.length > 0
                    ? `${experienceFilter.length} selected`
                    : "All Experience"
                }
                searchPlaceholder="Search experience..."
              />
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setFilters({ status: e.target.value as "all" | "test" | "live" })}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="all">All Status</option>
                <option value="live">Live Only</option>
                <option value="test">Test Only</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Type</label>
              <select
                value={typeFilter}
                onChange={(e) =>
                  setFilters({ type: e.target.value as "all" | "app_worker" | "regular" })
                }
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="all">All Types</option>
                <option value="app_worker">App Workers</option>
                <option value="regular">Regular Workers</option>
              </select>
            </div>

            {/* Gender Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Gender</label>
              <select
                value={genderFilter}
                onChange={(e) =>
                  setFilters({ gender: e.target.value as "all" | "Male" | "Female" })
                }
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="all">All Genders</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            {/* Location Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Location</label>
              <Input
                placeholder="Filter by location..."
                value={locationFilter === "all" ? "" : locationFilter}
                onChange={(e) => setFilters({ location: e.target.value || "all" })}
                className="h-9"
              />
            </div>

            {/* English Level Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">English Level</label>
              <select
                value={englishLevelFilter}
                onChange={(e) => setFilters({ englishLevel: e.target.value })}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="all">All English Levels</option>
                <option value="No English">No English</option>
                <option value="Basic">Basic</option>
                <option value="Conversational">Conversational</option>
                <option value="Fluent">Fluent</option>
                <option value="Native">Native</option>
              </select>
            </div>

            {/* Driver License Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Driver&apos;s License</label>
              <select
                value={driverLicenseFilter}
                onChange={(e) =>
                  setFilters({ hasDriverLicense: e.target.value as "all" | "Yes" | "No" })
                }
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="all">All</option>
                <option value="Yes">Has License</option>
                <option value="No">No License</option>
              </select>
            </div>

            {/* Car Filter */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Has Car</label>
              <select
                value={hasCarFilter}
                onChange={(e) => setFilters({ hasCar: e.target.value as "all" | "Yes" | "No" })}
                className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm"
              >
                <option value="all">All</option>
                <option value="Yes">Has Car</option>
                <option value="No">No Car</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Display */}
      {hasActiveFilters() && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Active filters:</span>
          {managerFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Manager: {getManagerName(managerFilter)}
              <button onClick={() => setFilters({ manager: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {agencyFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Agency: {agencyFilter === "none" ? "No Agency" : getAgencyName(agencyFilter)}
              <button onClick={() => setFilters({ agency: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {experienceFilter.length > 0 && (
            <Badge variant="secondary" className="gap-1">
              Experience: {experienceFilter.join(", ")}
              <button onClick={() => setFilters({ experience: [] })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {statusFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Status: {statusFilter}
              <button onClick={() => setFilters({ status: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {typeFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Type: {typeFilter === "app_worker" ? "App Workers" : "Regular Workers"}
              <button onClick={() => setFilters({ type: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {genderFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Gender: {genderFilter}
              <button onClick={() => setFilters({ gender: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {locationFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Location: {locationFilter}
              <button onClick={() => setFilters({ location: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {englishLevelFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              English: {englishLevelFilter}
              <button onClick={() => setFilters({ englishLevel: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {driverLicenseFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              License: {driverLicenseFilter}
              <button onClick={() => setFilters({ hasDriverLicense: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {hasCarFilter !== "all" && (
            <Badge variant="secondary" className="gap-1">
              Car: {hasCarFilter}
              <button onClick={() => setFilters({ hasCar: "all" })} className="ml-1">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      <DataTable<WorkerModel, unknown>
        columns={columns}
        data={filteredWorkers}
        isLoading={loading}
        disablePagination={true}
        searchColumnId="all"
        initialVisibility={{ all: false }}
        searchValue={localSearch}
        onSearchChange={handleSearchChange}
        emptyMessage={
          workers && workers.length > 0
            ? "No workers match the current filters."
            : "No workers found in the system."
        }
      />

      <AddWorkerDialog
        open={isAddModalOpen}
        onOpenChange={setIsAddModalOpen}
        showManagerField
        showAgencyField
        showPayRateFields
        showSpecialtiesField
        managers={getManagerOptions()}
        agencies={agencies}
        onSuccess={handleWorkerAdded}
      />

      <CreateWorkerAccountDialog
        open={isCreateAccountModalOpen}
        onOpenChange={setIsCreateAccountModalOpen}
        showManagerField
        showAgencyField
        showPayRateFields
        showSpecialtiesField
        managers={getManagerOptions()}
        agencies={agencies}
        onSuccess={handleWorkerAdded}
      />
    </div>
  );
}
